import React from 'react';
import './Footer.css';
import { useLanguage } from '../../context/LanguageContext';
import { 
  TwitterIcon, 
  DiscordIcon, 
  TelegramIcon, 
  GithubIcon, 
  LockIcon, 
  WarningIcon,
  getNetworkIcon 
} from '../common/Icons';

function Footer({ selectedNetwork, networkConfig }) {
  const { t } = useLanguage();
  const getNetworkDisplay = () => {
    if (networkConfig?.chainName) {
      const isTestnet = selectedNetwork.includes('testnet');
      return {
        name: networkConfig.chainName,
        class: isTestnet ? 'testnet' : 'mainnet'
      };
    }
    return {
      name: 'HuanChain Network',
      class: 'default'
    };
  };

  const networkDisplay = getNetworkDisplay();
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { name: 'Twitter', url: 'https://twitter.com', icon: <TwitterIcon size={16} /> },
    { name: 'Discord', url: 'https://discord.com', icon: <DiscordIcon size={16} /> },
    { name: 'Telegram', url: 'https://telegram.org', icon: <TelegramIcon size={16} /> },
    { name: 'GitHub', url: 'https://github.com', icon: <GithubIcon size={16} /> }
  ];

  const quickLinks = [
    { name: t('footer.docs'), url: '#' },
    { name: t('footer.bounty'), url: '#' },
    { name: t('footer.governance'), url: '#' },
    { name: t('footer.forum'), url: '#' }
  ];

  const protocolLinks = [
    { name: t('footer.analytics'), url: '#' },
    { name: t('footer.tokenLists'), url: '#' },
    { name: t('footer.brandAssets'), url: '#' },
    { name: t('footer.careers'), url: '#' }
  ];

  const legalLinks = [
    { name: t('footer.terms'), url: '#' },
    { name: t('footer.privacy'), url: '#' },
    { name: t('footer.cookie'), url: '#' },
    { name: t('footer.disclaimerTitle'), url: '#' }
  ];

  return (
    <footer className="footer">
      <div className="footer-container">
        {/* Main Footer Content */}
        <div className="footer-main">
          {/* Brand Section */}
          <div className="footer-brand">
            <div className="footer-logo">
              <img src="/logo.png" alt="HuanChain Logo" className="footer-huan-logo" />
              <span className="footer-brand-name">HuanChain</span>
            </div>
            <p className="footer-description">
              {t('footer.desc')}
            </p>
            <div className="footer-stats">
              <div className="stat-item">
                <div className="stat-value">$2.1B+</div>
                <div className="stat-label">{t('stats.volume')}</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">150K+</div>
                <div className="stat-label">{t('stats.users')}</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">500+</div>
                <div className="stat-label">{t('stats.tokens')}</div>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div className="footer-section">
            <h3 className="footer-section-title">{t('footer.protocol')}</h3>
            <ul className="footer-links">
              {quickLinks.map((link, index) => (
                <li key={index}>
                  <a href={link.url} className="footer-link">
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Protocol Links */}
          <div className="footer-section">
            <h3 className="footer-section-title">{t('footer.ecosystem')}</h3>
            <ul className="footer-links">
              {protocolLinks.map((link, index) => (
                <li key={index}>
                  <a href={link.url} className="footer-link">
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal Links */}
          <div className="footer-section">
            <h3 className="footer-section-title">{t('footer.legal')}</h3>
            <ul className="footer-links">
              {legalLinks.map((link, index) => (
                <li key={index}>
                  <a href={link.url} className="footer-link">
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Community & Social */}
          <div className="footer-section">
            <h3 className="footer-section-title">{t('footer.community')}</h3>
            <div className="social-links">
              {socialLinks.map((social, index) => (
                <a 
                  key={index}
                  href={social.url} 
                  target="_blank"
                  rel="noopener noreferrer"
                  className="social-link"
                  title={social.name}
                >
                  <span className="social-icon">{social.icon}</span>
                  <span className="social-name">{social.name}</span>
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Footer Bottom */}
        <div className="footer-bottom">
          <div className="footer-bottom-left">
            <p className="copyright">
              © {currentYear} HuanChain. {t('footer.allRightsReserved')}
            </p>
            <div className="version-info">
              <span className="version">v2.1.0</span>
              <span className="build">Build #1234</span>
            </div>
          </div>
          
          <div className="footer-bottom-right">
            <div className="network-info">
              <div className={`network-badge ${networkDisplay.class}`}>
                <div className="network-dot active"></div>
                <span className="network-badge-icon">{getNetworkIcon(selectedNetwork, 16)}</span>
                <span className="network-badge-name">{networkDisplay.name}</span>
              </div>
            </div>
            <div className="disclaimer" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
              <WarningIcon size={14} color="#f59e0b" />
              <span className="disclaimer-text">
                {t('footer.riskWarning')}
              </span>
            </div>
          </div>
        </div>

        {/* Security Notice */}
        <div className="security-notice">
          <div className="security-content">
            <div className="security-icon">
              <LockIcon size={18} color="#10b981" />
            </div>
            <div className="security-text">
              <strong>{t('footer.securityNotice')}:</strong> {t('footer.securityDesc')}
            </div>
          </div>
        </div>

        {/* Required: DEMO PURPOSE NOTICE */}
        <div className="footer-demo-notice">
          <div className="demo-notice-pill">
            <span className="demo-pulse-dot"></span>
            <span className="demo-notice-text">just ONLY DEMO PURPOSE</span>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;